import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/api/axiosInstance";
import { type AdminMaterial } from "./useAdmin";

// Materials CRUD hooks
export const useMaterials = (enabled: boolean = true) => {
    return useQuery<AdminMaterial[]>({
        queryKey: ['admin', 'materials'],
        queryFn: async () => {
            const { data } = await axiosInstance.get('/products/materials/');
            return data.results || data;
        },
        enabled,
    });
};

export const useMaterial = (id: number, enabled: boolean = true) => {
    return useQuery<AdminMaterial>({
        queryKey: ['admin', 'materials', id],
        queryFn: async () => {
            const { data } = await axiosInstance.get(`/products/materials/${id}/`);
            return data;
        },
        enabled: enabled && !!id,
    });
};

export const useCreateMaterial = () => {
    const queryClient = useQueryClient();

    return useMutation<AdminMaterial, Error, Omit<AdminMaterial, 'id' | 'created_at' | 'updated_at'>>({
        mutationFn: async (materialData) => {
            const { data } = await axiosInstance.post('/products/materials/', materialData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'materials'] });
        },
    });
};

export const useUpdateMaterial = () => {
    const queryClient = useQueryClient();

    return useMutation<AdminMaterial, Error, { id: number; data: Partial<Omit<AdminMaterial, 'id' | 'created_at' | 'updated_at'>> }>({
        mutationFn: async ({ id, data }) => {
            const { data: response } = await axiosInstance.patch(`/products/materials/${id}/`, data);
            return response;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'materials'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'materials', id] });
        },
    });
};

export const useDeleteMaterial = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: async (id: number) => {
            await axiosInstance.delete(`/products/materials/${id}/`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'materials'] });
        },
    });
};
